import { useState, useCallback } from "react";
import { useAuth } from "providers/auth";
import { envConfig } from "Constant";
import { ChatStateType, MessageType, ChatResponseType, ChatContextType } from "./types";
import { CHAT_ENDPOINT } from "./constants";

export default function useFeedbackChat() {
  const { user } = useAuth();
  const [state, setState] = useState<ChatStateType>({
    messages: [],
    isLoading: false,
    issueCreated: null,
  });
  const [images, setImages] = useState<string[]>([]);

  const buildContext = useCallback((): ChatContextType => ({
    user_id: user?.id ?? "",
    user_email: user?.email ?? "",
    current_route: window.location.pathname,
    browser: navigator.userAgent,
    platform: "web",
    app_version: envConfig.APP_VERSION,
    timestamp: new Date().toISOString(),
  }), [user]);

  const sendMessage = useCallback(async (content: string) => {
    const userMessage: MessageType = { role: "user", content };
    const updatedMessages = [...state.messages, userMessage];

    setState((prev) => ({
      ...prev,
      messages: updatedMessages,
      isLoading: true,
    }));

    try {
      const response = await fetch(CHAT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: updatedMessages,
          context: buildContext(),
          images: images.length > 0 ? images : undefined,
        }),
      });

      if (!response.ok) {
        throw new Error("Server error");
      }

      const data: ChatResponseType = await response.json();
      const assistantMessage: MessageType = {
        role: "assistant",
        content: data.message,
      };

      setState((prev) => ({
        ...prev,
        messages: [...updatedMessages, assistantMessage],
        isLoading: false,
        issueCreated:
          data.type === "issue_created" && data.issue_url && data.issue_id
            ? { url: data.issue_url, id: data.issue_id }
            : prev.issueCreated,
      }));
    } catch {
      const errorMessage: MessageType = {
        role: "assistant",
        content: "Something went wrong, please try again.",
      };
      setState((prev) => ({
        ...prev,
        messages: [...updatedMessages, errorMessage],
        isLoading: false,
      }));
    }

  }, [state.messages, images, buildContext]);

  const addImage = useCallback((base64: string) => {
    setImages((prev) => [...prev, base64]);
  }, []);

  const removeImage = useCallback((index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const reset = useCallback(() => {
    setState({ messages: [], isLoading: false, issueCreated: null });
    setImages([]);
  }, []);

  return {
    ...state,
    images,
    sendMessage,
    addImage,
    removeImage,
    reset,
  };
}
